import React from 'react';
import { MapPin, CreditCard, Landmark, ShieldCheck, Loader2 } from 'lucide-react';
import { tx } from '@/translations/translations';

const FreeShippingBadge = ({ itemCount, language }) => {
  if (itemCount >= 5) {
    return (
      <div className="mb-4 flex items-center gap-2.5 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl px-4 py-3">
        <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
          <ShieldCheck className="w-4 h-4 text-white" />
        </div>
        <p className="text-sm font-bold text-green-700">
          {tx(language, 'Envío gratis aplicado', 'Free shipping applied', 'משלוח חינם הופעל')}
        </p>
      </div>
    );
  }
  const remaining = 5 - itemCount;
  const plural = remaining === 1 ? '' : 's';
  return (
    <div className="mb-4 flex items-center gap-2.5 bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5">
      <p className="text-xs text-slate-500">
        {tx(language,
          `Agrega ${remaining} producto${plural} más para envío gratis`,
          `Add ${remaining} more product${plural} for free shipping`,
          `הוסיפו עוד ${remaining} למשלוח חינם`)}
      </p>
    </div>
  );
};

export const PaymentMethodSelector = ({ language, text, itemCount, mpProcessing, onMercadoPago, onSelectMethod }) => (
  <div data-testid="payment-methods">
    <div className="mb-4 flex items-center gap-2.5 bg-gradient-to-r from-cyan-50 to-blue-50 border border-cyan-200 rounded-xl px-4 py-3">
      <div className="w-8 h-8 rounded-full bg-cyan-500 flex items-center justify-center flex-shrink-0">
        <MapPin className="w-4 h-4 text-white" />
      </div>
      <div>
        <p className="text-sm font-bold text-cyan-700">{tx(language, 'Envío a Domicilio', 'Home Delivery', 'משלוח עד הבית')}</p>
        <p className="text-[11px] text-cyan-600/70">{tx(language, 'CDMX y Edo. de México · 24-48 hrs', 'CDMX & Estado de México · 24-48 hrs', 'מקסיקו סיטי והמדינה · 24-48 שעות')}</p>
      </div>
    </div>

    <FreeShippingBadge itemCount={itemCount} language={language} />

    <h3 className="font-semibold text-slate-800 mb-2 text-base">{text.selectMethod}</h3>
    <p className="text-xs text-slate-400 mb-4 flex items-center gap-1">
      <ShieldCheck className="w-3.5 h-3.5 text-green-500" />
      {tx(language, 'Todos los pagos son seguros y encriptados', 'All payments are secure and encrypted', 'כל התשלומים מאובטחים ומוצפנים')}
    </p>
    <div className="space-y-3">
      <button
        onClick={onMercadoPago}
        disabled={mpProcessing}
        className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-slate-200 hover:border-cyan-500 hover:shadow-md bg-white transition-all duration-200 group"
        data-testid="pay-mercadopago-btn"
      >
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
          <CreditCard className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1 text-left">
          <p className="font-semibold text-slate-800 text-sm">
            {tx(language, 'Tarjeta de Crédito / Débito', 'Credit / Debit Card', 'כרטיס אשראי / חיוב')}
          </p>
          <p className="text-xs text-slate-400 mt-0.5">Visa, Mastercard, AMEX</p>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <img src="https://img.icons8.com/color/32/visa.png" alt="Visa" className="h-6" />
          <img src="https://img.icons8.com/color/32/mastercard-logo.png" alt="Mastercard" className="h-6" />
          <img src="https://img.icons8.com/color/32/amex.png" alt="Amex" className="h-6" />
        </div>
        {mpProcessing && <Loader2 className="w-5 h-5 animate-spin text-cyan-500 ml-2" />}
      </button>

      <button
        onClick={onMercadoPago}
        disabled={mpProcessing}
        className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-slate-200 hover:border-[#009ee3] hover:shadow-md bg-white transition-all duration-200 group"
        data-testid="pay-mercadopago-oxxo-btn"
      >
        <div className="w-12 h-12 rounded-xl bg-[#009ee3] flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
          <img src="https://customer-assets.emergentagent.com/job_5e7b42a7-5c5c-4b44-834b-4b4d13f59f66/artifacts/6pbq7gks_image.png" alt="Mercado Pago" className="h-7 w-7 object-contain" />
        </div>
        <div className="flex-1 text-left">
          <p className="font-semibold text-slate-800 text-sm">Mercado Pago</p>
          <p className="text-xs text-slate-400 mt-0.5">
            {tx(language, 'OXXO, SPEI, Meses sin intereses', 'OXXO, SPEI, Monthly installments', 'OXXO, SPEI, תשלומים')}
          </p>
        </div>
        {mpProcessing && <Loader2 className="w-5 h-5 animate-spin text-[#009ee3] ml-2" />}
      </button>

      <button
        onClick={() => onSelectMethod('paypal')}
        className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-slate-200 hover:border-[#0070ba] hover:shadow-md bg-white transition-all duration-200 group"
        data-testid="pay-paypal-btn"
      >
        <div className="w-12 h-12 rounded-xl bg-[#003087] flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
          <span className="font-bold text-lg text-white tracking-tight">P</span>
        </div>
        <div className="flex-1 text-left">
          <p className="font-semibold text-slate-800 text-sm">PayPal</p>
          <p className="text-xs text-slate-400 mt-0.5">
            {tx(language, 'Paga con tu cuenta PayPal', 'Pay with your PayPal account', 'שלמו עם חשבון PayPal')}
          </p>
        </div>
        <span className="text-[#003087] font-bold text-base tracking-tight flex-shrink-0">Pay<span className="text-[#0070ba]">Pal</span></span>
      </button>

      <button
        onClick={() => onSelectMethod('transfer')}
        className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-slate-200 hover:border-slate-400 hover:shadow-md bg-white transition-all duration-200 group"
        data-testid="pay-transfer-btn"
      >
        <div className="w-12 h-12 rounded-xl bg-slate-700 flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
          <Landmark className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1 text-left">
          <p className="font-semibold text-slate-800 text-sm">{text.payTransfer}</p>
          <p className="text-xs text-slate-400 mt-0.5">SPEI / CLABE</p>
        </div>
      </button>
    </div>

    <div className="mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 pt-4 border-t border-slate-100">
      <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
        <ShieldCheck className="w-3.5 h-3.5 text-green-600" />
        <span className="font-medium">{tx(language, 'Pago 100% seguro', '100% secure payment', 'תשלום מאובטח 100%')}</span>
      </div>
      <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
        <svg className="w-3.5 h-3.5 text-slate-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
        <span className="font-medium">{tx(language, 'Encriptación SSL', 'SSL encryption', 'הצפנת SSL')}</span>
      </div>
      <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
        <svg className="w-3.5 h-3.5 text-slate-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
        <span className="font-medium">{tx(language, 'Tus datos protegidos', 'Your data protected', 'הנתונים שלכם מוגנים')}</span>
      </div>
    </div>
  </div>
);
